import { useEffect, useRef, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

import { useLocalization } from "@i18n";

import type {
  InspectionChartMonth,
  InspectionChartResponse,
} from "../../api/inspection";
import {
  INSPECTION_CHART_PERIODS,
  formatInspectionChartMonthLabel,
  inspectionChartPeriodMonthsI18nKey,
  type InspectionChartPeriodMonths,
} from "../../utils/inspectionChartPeriod";

export interface InspectionEvolutionChartProps {
  chart?: InspectionChartResponse;
  isLoading?: boolean;
  errorMessage?: string | null;
  periodMonths?: InspectionChartPeriodMonths;
  onPeriodChange?: (months: InspectionChartPeriodMonths) => void;
}

type SeriesKey = "total" | "finalized" | "notFinalized" | "scheduled" | "manual";

/**
 * Cores das séries = mesmas da tabela (inspection.md §6.5). Forma visual =
 * barras agrupadas do protótipo navegável (alternativa aceita no PRD).
 */
const SERIES: { key: SeriesKey; cssVar: string }[] = [
  { key: "total", cssVar: "var(--insp-ch-total)" },
  { key: "finalized", cssVar: "var(--insp-ch-done)" },
  { key: "notFinalized", cssVar: "var(--insp-ch-missed)" },
  { key: "scheduled", cssVar: "var(--insp-ch-prog)" },
  { key: "manual", cssVar: "var(--insp-ch-nprog)" },
];

const TIP_PAD = 16;

const monthKey = (month: InspectionChartMonth) =>
  `${month.year}-${String(month.month).padStart(2, "0")}`;

const chartYScale = (maxVal: number) => {
  const top = Math.max(2, Math.ceil(maxVal / 2) * 2);
  const ticks = [top, Math.round((top * 2) / 3), Math.round(top / 3), 0].filter(
    (value, index, list) => list.indexOf(value) === index,
  );
  return { top, ticks };
};

const tipStyleFromPointer = (clientX: number, clientY: number) => {
  const tipWidth = 220;
  const tipHeight = 180;
  let left = clientX + TIP_PAD;
  let top = clientY + TIP_PAD;

  if (left + tipWidth > window.innerWidth - 8) {
    left = clientX - tipWidth - TIP_PAD;
  }
  if (top + tipHeight > window.innerHeight - 8) {
    top = clientY - tipHeight - TIP_PAD;
  }

  return {
    left: Math.max(8, left),
    top: Math.max(8, top),
  };
};

/**
 * Gráfico de acompanhamento (inspection.md §6.5): multi-série por mês,
 * legenda só visual, tooltip flutuante seguindo o cursor (protótipo).
 */
const InspectionEvolutionChart = ({
  chart,
  isLoading = false,
  errorMessage = null,
  periodMonths = 12,
  onPeriodChange,
}: InspectionEvolutionChartProps) => {
  const { translate } = useLocalization();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(
    null,
  );
  const [periodMenu, setPeriodMenu] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const periodBtnRef = useRef<HTMLButtonElement | null>(null);

  const periodLabel = translate(inspectionChartPeriodMonthsI18nKey(periodMonths));
  const monthLabel = (month: InspectionChartMonth) =>
    formatInspectionChartMonthLabel(month.year, month.month, translate);
  const canPickPeriod = Boolean(onPeriodChange);

  const months = chart?.months ?? [];
  const maxValue = Math.max(
    0,
    ...months.flatMap((month) => SERIES.map((series) => month[series.key])),
  );
  const { top, ticks } = useMemo(() => chartYScale(maxValue), [maxValue]);

  const hoveredMonth =
    months.find((month) => monthKey(month) === hoveredKey) ?? null;

  const showTip = (key: string, clientX: number, clientY: number) => {
    setHoveredKey(key);
    setTipPos(tipStyleFromPointer(clientX, clientY));
  };

  const hideTip = () => {
    setHoveredKey(null);
    setTipPos(null);
  };

  useEffect(() => {
    if (!periodMenu) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (periodBtnRef.current?.contains(target)) return;
      const pop = document.querySelector("[data-testid='inspection-period-menu']");
      if (pop?.contains(target)) return;
      setPeriodMenu(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [periodMenu]);

  const openPeriodMenu = () => {
    const btn = periodBtnRef.current;
    if (!btn || !canPickPeriod) return;

    const rect = btn.getBoundingClientRect();
    const width = 200;
    let left = rect.left;
    if (left + width > window.innerWidth - 12) {
      left = window.innerWidth - width - 12;
    }
    setPeriodMenu({ top: rect.bottom + 6, left: Math.max(8, left) });
  };

  return (
    <section
      aria-label={translate("inspection.chart.title")}
      className="insp-card insp-chart-card"
    >
      <div className="insp-chart-head">
        <h2 className="insp-chart-title">
          {translate("inspection.chart.title")}
        </h2>
        {periodLabel ? (
          <button
            ref={periodBtnRef}
            type="button"
            className="insp-period-chip"
            aria-haspopup="menu"
            aria-expanded={periodMenu !== null}
            disabled={!canPickPeriod}
            onClick={openPeriodMenu}
          >
            {translate("inspection.chart.period")}{" "}
            <strong>{periodLabel}</strong>
            <ChevronDown aria-hidden size={14} />
          </button>
        ) : null}
      </div>

      <ul data-testid="inspection-chart-legend" className="insp-chart-legend">
        {SERIES.map((series) => (
          <li key={series.key}>
            <i aria-hidden style={{ background: series.cssVar }} />
            <span>{translate(`inspection.chart.series.${series.key}`)}</span>
          </li>
        ))}
      </ul>

      {errorMessage ? (
        <p role="alert" className="text-sm text-red-600">
          {errorMessage}
        </p>
      ) : isLoading ? (
        <div className="h-[120px] animate-pulse rounded-lg bg-[var(--insp-surface-2)]" />
      ) : months.length === 0 ? (
        <div className="insp-chart-empty">
          {translate("inspection.chart.empty")}
        </div>
      ) : (
        <div className="insp-chart-plot">
          <div className="insp-chart-yaxis" aria-hidden>
            {ticks.map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
          <div className="insp-chart-cols">
            {months.map((month) => {
              const key = monthKey(month);
              const isHl = hoveredKey === key;
              const values = SERIES.map((series) => month[series.key]);

              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`inspection-chart-point-${key}`}
                  className={`insp-chart-month${isHl ? " is-hl" : ""}`}
                  onMouseEnter={(event) =>
                    showTip(key, event.clientX, event.clientY)
                  }
                  onMouseMove={(event) =>
                    showTip(key, event.clientX, event.clientY)
                  }
                  onMouseLeave={hideTip}
                  onFocus={(event) => {
                    const rect = event.currentTarget.getBoundingClientRect();
                    showTip(key, rect.left + rect.width / 2, rect.top);
                  }}
                  onBlur={hideTip}
                  aria-label={monthLabel(month)}
                >
                  <div className="insp-chart-bars">
                    {values.map((value, index) => (
                      <span
                        key={SERIES[index]!.key}
                        className="insp-bar"
                        style={{
                          height: `${maxValue ? Math.max(2, (value / top) * 100) : 2}%`,
                          background: SERIES[index]!.cssVar,
                        }}
                      />
                    ))}
                  </div>
                  <span className="insp-chart-xlabel">
                    {monthLabel(month)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {periodMenu
        ? createPortal(
            <div
              data-testid="inspection-period-menu"
              role="menu"
              className="insp-menu-pop"
              style={{ top: periodMenu.top, left: periodMenu.left }}
            >
              {INSPECTION_CHART_PERIODS.map((months) => {
                const selected = months === periodMonths;
                return (
                  <button
                    key={months}
                    type="button"
                    role="menuitem"
                    className={selected ? "sel" : undefined}
                    onClick={() => {
                      onPeriodChange?.(months);
                      setPeriodMenu(null);
                    }}
                  >
                    {selected ? (
                      <Check aria-hidden size={16} />
                    ) : (
                      <span aria-hidden style={{ width: 16 }} />
                    )}
                    {translate(inspectionChartPeriodMonthsI18nKey(months))}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}

      {hoveredMonth && tipPos
        ? createPortal(
            <div
              data-testid="inspection-chart-tooltip"
              role="tooltip"
              className="insp-chart-tip"
              style={{ left: tipPos.left, top: tipPos.top }}
            >
              <p className="insp-ct-title">{monthLabel(hoveredMonth)}</p>
              {SERIES.map((series) => (
                <div key={series.key} className="insp-ct-row">
                  <span
                    className="insp-ct-dot"
                    aria-hidden
                    style={{ background: series.cssVar }}
                  />
                  <span className="insp-ct-name">
                    {translate(`inspection.chart.series.${series.key}`)}
                  </span>
                  <span className="insp-ct-val">
                    {hoveredMonth[series.key]}
                  </span>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </section>
  );
};

export default InspectionEvolutionChart;
